import { readableColor } from 'polished';
import styled, { css, keyframes } from 'styled-components';
import { convertColorToThemeHexCode } from 'Styles/colors.util';
import readableText from 'Styles/readable-text';
import { getStyle } from 'Styles/cardFontStyles';

import { BORDER_RADIUS } from '../Card/Card.styled';
import { Button as StyledButton } from '../Button/Button.styled';

const LARGE_CTA_PADDING = '13px 16px';
const MEDIUM_CTA_PADDING = '10px 16px';
const SMALL_CTA_PADDING = '6px 16px';

const blur = keyframes`
  0% {
    filter: blur(0);
  }

  100% {
    filter: blur(20px);
  }
`;

export const TextCard = styled.div`
  background-color: ${({ $backgroundColor }) =>
    convertColorToThemeHexCode($backgroundColor)};
  border-radius: ${BORDER_RADIUS};
  grid-column: ${({ $gridSpan }) => `span ${$gridSpan}`};
  position: relative;
  ${({ $areCtasRevealed }) => $areCtasRevealed && 'overflow: hidden;'}
  ${({ $isClickable }) => $isClickable && 'cursor: pointer;'}
  ${({ as }) => as === 'a' && 'text-decoration: none;'}
  
  display: flex;
  ${({ $alignment }) => $alignment === 'center' && 'justify-content: center;'}
  align-items: flex-start;
`;

export const Cta = styled(StyledButton)`
  margin: 4px 8px 8px 0;
  ${({ $backgroundColor }) => readableText(readableColor($backgroundColor))};
  overflow-wrap: anywhere;

  /* When text wraps, specifying a top/bottom padding will keeps wrapping text away from the edges. */
  ${({ size = 'small' }) => {
    if (size === 'large') return `padding: ${LARGE_CTA_PADDING};`;
    if (size === 'medium') return `padding: ${MEDIUM_CTA_PADDING};`;
    if (size === 'small') return `padding: ${SMALL_CTA_PADDING};`;
  }}

  &:last-of-type {
    margin-right: 0px;
  }
`;

export const Subheader = styled.div`
  a {
    color: unset;
    text-decoration: unset;
  }

  h2 {
    ${({ $responsiveStyleSize = 'regular' }) =>
      getStyle($responsiveStyleSize, 'title')}
    margin-bottom: 4px;
  }

  p {
    ${({ $responsiveStyleSize = 'regular' }) =>
      getStyle($responsiveStyleSize, 'text')}
  }
`;

export const Title = styled.h3`
  ${({ $responsiveStyleSize = 'regular' }) =>
    getStyle($responsiveStyleSize, 'title')}
  color: ${({ $backgroundColor }) => readableColor($backgroundColor)};
  margin-bottom: 4px;
`;

export const Copy = styled.div`
  color: ${({ $backgroundColor }) => readableColor($backgroundColor)};
  text-align: ${({ $alignment }) => $alignment};
  ${({ $areCtasRevealed }) =>
    $areCtasRevealed &&
    css`
      animation: ${blur} 200ms cubic-bezier(0.25, 1, 0.25, 1);
      animation-fill-mode: forwards;
    `}

  ${({ $responsiveStyleSize = 'regular', $withPadding }) => {
    const paddingValue = getStyle($responsiveStyleSize, 'padding');
    const font = getStyle($responsiveStyleSize || 'regular', 'text');
    return `${font}; padding: ${$withPadding ? paddingValue : '0'};`;
  }}

  & ${Cta} {
    margin-top: 8px;
  }

  & ${Title} + ${Cta} {
    margin-top: 4px;
  }

  *:last-child {
    margin-bottom: 0;
  }
`;

export const Ctas = styled.div`
  align-items: center;
  border-radius: ${BORDER_RADIUS};
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  width: 100%;
  padding: ${({ $responsiveStyleSize }) =>
    $responsiveStyleSize === 'mega' ? '24px' : '12px'};

  ${Cta} {
    margin: 8px 0 0 0;
    max-width: 250px;
    width: 100%;
    text-align: center;
  }

  ${Cta}:first-of-type {
    margin-top: 0;
  }
`;

export const CtasBackdrop = styled.div`
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  background-color: var(--hover);
  opacity: 0.5;
`;

export const CtasContainer = styled.div`
  border-radius: ${BORDER_RADIUS};
  height: calc(100% + 2px);
  position: absolute;
  left: 0;
  top: -2px;
  width: 100%;
`;

export const Link = styled.a`
  text-decoration: unset;
  color: unset;
`;

export const Icon = styled.img`
  height: 48px;
  margin-bottom: 8px;
  ${({ $alignment }) =>
    $alignment === 'center' && 'margin-left: auto; margin-right: auto;'}
`;

export default {};
